import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { 
  Download, 
  FileText, 
  Mail, 
  Trash2, 
  AlertTriangle,
  Calendar,
  Users,
  BarChart3,
  Database
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useSubscription } from "@/hooks/use-subscription";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { 
  AlertDialog, 
  AlertDialogAction, 
  AlertDialogCancel, 
  AlertDialogContent, 
  AlertDialogDescription, 
  AlertDialogFooter, 
  AlertDialogHeader, 
  AlertDialogTitle, 
  AlertDialogTrigger 
} from "@/components/ui/alert-dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

interface AccountSettingsProps {
  user: any;
  preferences: any;
  onChange: (category: string, key: string, value: any) => void;
}

export default function AccountSettings({ user, preferences, onChange }: AccountSettingsProps) {
  const [isExporting, setIsExporting] = useState(false);
  const [isSendingReport, setIsSendingReport] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [exportFormat, setExportFormat] = useState<'csv' | 'json' | 'pdf'>('csv');
  const [reportType, setReportType] = useState<'monthly' | 'quarterly' | 'annual'>('monthly');
  const { toast } = useToast();
  const { subscription, planType, isLoading: isSubscriptionLoading } = useSubscription();

  const accountTypeLabel = isSubscriptionLoading
    ? "Loading..."
    : subscription?.isTrialUser || planType === "trial"
      ? "Free Trial"
      : subscription?.plan?.name
        ? `${subscription.plan.name} Plan`
        : "Free Plan";

  const statusLabel = subscription?.plan?.status === "trialing" || subscription?.status === "trialing"
    ? "Trialing"
    : subscription?.isActive || subscription?.hasActiveSubscription
      ? "Active"
      : "Inactive";

  const handleExportData = async () => {
    setIsExporting(true);
    try {
      // Use raw fetch so we can call .blob() — apiRequest always parses as JSON
      // which breaks CSV/text responses and causes the "Export Failed" error.
      const response = await fetch(`/api/export/data?format=${exportFormat}`, {
        method: 'GET',
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error(`Server returned ${response.status}`);
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = url;
      // PDF export is delivered as plain text by the server
      const ext = exportFormat === 'pdf' ? 'txt' : exportFormat;
      a.download = `saasgama-data-export.${ext}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);

      toast({
        title: "Export Successful",
        description: `Your data has been exported as ${exportFormat.toUpperCase()} format.`,
      });
    } catch (error) {
      toast({
        title: "Export Failed",
        description: "There was an error exporting your data. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsExporting(false);
    }
  };

  const handleSendReport = async () => {
    setIsSendingReport(true);
    try {
      const response = await apiRequest('/api/reports/send', {
        method: 'POST',
        body: {
          type: reportType,
          email: user?.email
        }
      });

      if (response.ok) {
        toast({
          title: "Report Sent",
          description: `${reportType.charAt(0).toUpperCase() + reportType.slice(1)} report has been sent to ${user?.email}.`
        });
      }
    } catch (error) {
      toast({
        title: "Send Failed",
        description: "There was an error sending your report. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsSendingReport(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (confirmText !== "DELETE MY ACCOUNT") {
      toast({
        title: "Confirmation required",
        description: "Please type 'DELETE MY ACCOUNT' to confirm deletion.",
        variant: "destructive",
      });
      return;
    }

    setIsDeleting(true);
    try {
      const response = await apiRequest('/api/user/delete-account', {
        method: 'DELETE'
      });

      // apiRequest returns the response data directly, not a Response object
      if (response?.success) {
        toast({
          title: "Account Deleted",
          description: "Your account has been permanently deleted."
        });
        // Clear all cached data and redirect to home page
        queryClient.clear();
        window.location.href = '/';
      } else {
        throw new Error('Delete failed');
      }
    } catch (error: any) {
      // Check if it's an authentication error
      if (error.message?.includes('401') || error.message?.includes('Authentication required')) {
        toast({
          title: "Authentication Required",
          description: "Please log in again to delete your account.",
          variant: "destructive"
        });
        // Clear cached auth state and redirect to login
        queryClient.setQueryData(["/api/user"], null);
        window.location.href = '/login';
      } else {
        toast({
          title: "Deletion Failed",
          description: error.message || "There was an error deleting your account. Please try again.",
          variant: "destructive"
        });
      }
    } finally {
      setIsDeleting(false);
      setConfirmText("");
    }
  };

  return (
    <div className="space-y-6">
      {/* Account Overview */}
      <Card className="border-0 shadow-lg bg-white/50 backdrop-blur-sm">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5 text-blue-600" />
            Account Overview
          </CardTitle>
          <CardDescription>
            Your account status and subscription information
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="p-4 bg-blue-50 rounded-lg">
              <div className="text-sm font-medium text-blue-600">Account Type</div>
              <div className="text-lg font-semibold">{accountTypeLabel}</div>
            </div>
            <div className="p-4 bg-green-50 rounded-lg">
              <div className="text-sm font-medium text-green-600">Status</div>
              <div className="text-lg font-semibold flex items-center gap-2">
                {statusLabel}
                <Badge variant="outline" className="bg-green-100 text-green-800 border-green-300">
                  Verified
                </Badge>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Data Export */}
      <Card className="border-0 shadow-lg bg-white/50 backdrop-blur-sm">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Download className="h-5 w-5 text-indigo-600" />
            Export Data
          </CardTitle>
          <CardDescription>
            Download your data in various formats for backup or migration
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-col sm:flex-row sm:items-end gap-4">
            <div className="flex-1">
              <label className="text-sm font-medium">Export Format</label>
              <Select value={exportFormat} onValueChange={(value: 'csv' | 'json' | 'pdf') => setExportFormat(value)}>
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="csv">CSV - Spreadsheet format</SelectItem>
                  <SelectItem value="json">JSON - Structured data</SelectItem>
                  <SelectItem value="pdf">PDF - Readable report</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button 
              onClick={handleExportData}
              disabled={isExporting}
              className="w-full sm:w-auto"
            >
              {isExporting ? (
                "Exporting..."
              ) : (
                <>
                  <Download className="h-4 w-4 mr-2" />
                  Export Data
                </>
              )}
            </Button>
          </div>
          <div className="text-xs text-slate-500">
            Export includes: Applications, Licenses, Teams, Employees, and Usage Statistics
          </div>
        </CardContent>
      </Card>

      {/* Reports */}
      <Card className="border-0 shadow-lg bg-white/50 backdrop-blur-sm">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BarChart3 className="h-5 w-5 text-purple-600" />
            Send Reports
          </CardTitle>
          <CardDescription>
            Generate and send detailed reports to your email
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-4">
            <div className="flex-1">
              <label className="text-sm font-medium">Report Type</label>
              <Select value={reportType} onValueChange={(value: 'monthly' | 'quarterly' | 'annual') => setReportType(value)}>
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="monthly">Monthly Report</SelectItem>
                  <SelectItem value="quarterly">Quarterly Summary</SelectItem>
                  <SelectItem value="annual">Annual Overview</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button 
              onClick={handleSendReport}
              disabled={isSendingReport}
              className="mt-6"
              variant="outline"
            >
              {isSendingReport ? (
                "Sending..."
              ) : (
                <>
                  <Mail className="h-4 w-4 mr-2" />
                  Send Report
                </>
              )}
            </Button>
          </div>
          <div className="text-xs text-slate-500">
            Reports will be sent to: {user?.email || 'your registered email'}
          </div>
        </CardContent>
      </Card>

      {/* Account Management */}
      <Card className="border-0 shadow-lg bg-white/50 backdrop-blur-sm">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Database className="h-5 w-5 text-slate-600" />
            Account Management
          </CardTitle>
          <CardDescription>
            Manage your account settings and data
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-3">
            <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
              <div>
                <div className="font-medium">Data Retention</div>
                <div className="text-sm text-slate-600">Your data is automatically backed up daily</div>
              </div>
              <Badge variant="outline" className="bg-slate-100">
                Active
              </Badge>
            </div>
            
            <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
              <div>
                <div className="font-medium">Account Sync</div>
                <div className="text-sm text-slate-600">Real-time synchronization across devices</div>
              </div>
              <Badge variant="outline" className="bg-green-100 text-green-800">
                Enabled
              </Badge>
            </div>
          </div>
        </CardContent>
      </Card>

      <Separator className="my-8" />

      {/* Danger Zone */}
      <Card className="border-red-200 shadow-lg bg-red-50/50 backdrop-blur-sm">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-red-700">
            <AlertTriangle className="h-5 w-5" />
            Danger Zone
          </CardTitle>
          <CardDescription className="text-red-600">
            Irreversible actions that will permanently affect your account
          </CardDescription>
        </CardHeader>
        <CardContent>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button 
                variant="destructive" 
                className="w-full"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Delete Account Permanently
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle className="flex items-center gap-2">
                  <AlertTriangle className="h-5 w-5 text-red-500" />
                  Are you absolutely sure?
                </AlertDialogTitle>
                <AlertDialogDescription className="space-y-4">
                  <p>This action cannot be undone. This will permanently delete your account and remove all your data from our servers.</p>
                  <p className="font-medium">This includes:</p>
                  <ul className="list-disc list-inside space-y-1 text-sm">
                    <li>All applications and licenses</li>
                    <li>Team and employee data</li>
                    <li>Usage statistics and reports</li>
                    <li>Account settings and preferences</li>
                  </ul>
                  <div className="mt-4 space-y-2">
                    <Label htmlFor="confirmText" className="text-sm font-medium text-red-700">
                      Type "DELETE MY ACCOUNT" to confirm:
                    </Label>
                    <Input
                      id="confirmText"
                      type="text"
                      value={confirmText}
                      onChange={(e) => setConfirmText(e.target.value)}
                      placeholder="DELETE MY ACCOUNT"
                      className="border-red-300 focus:border-red-500 focus:ring-red-500"
                    />
                  </div>
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  onClick={handleDeleteAccount}
                  disabled={isDeleting || confirmText !== "DELETE MY ACCOUNT"}
                  className="bg-red-600 hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isDeleting ? "Deleting..." : "Yes, delete my account"}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </CardContent>
      </Card>
    </div>
  );
}
